/**
 * ContactDetail 联系人详情页：基本信息 + 互动 / 承诺 / 重要日期 / 礼物 / 社交链接 / 关系。
 * 数据来源 ContactDetail（内嵌 interactions/importantDates/openCommitments/gifts/socialLinks/relations）。
 * 所有新建请求体按严格白名单拼装，可选字段仅在已填时提交。
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useContact, useUpdateContact, useDeleteContact, useArchiveContact, useContacts,
  useCreateInteraction, useDeleteInteraction,
  useCreateCommitment, useCompleteCommitment, useDeleteCommitment,
  useCreateImportantDate, useCreateGift, useDeleteGift,
  useCreateLink, useDeleteLink, useCreateRelation, useDeleteRelation,
} from '../hooks/useContacts';
import { ContactForm } from '../components/ContactForm';
import {
  Card, Button, Input, Select, Badge, Spinner, Tabs, ConfirmDialog, EmptyState,
  errMsg, SectionTitle,
} from '../components/ui';
import { Icon } from '../components/icons';
import type {
  ContactDetail as ContactDetailT, InteractionType, DateKind, GiftDirection,
  CreateInteractionBody, CreateCommitmentBody, CreateImportantDateBody,
  CreateGiftBody, CreateLinkBody, CreateRelationBody,
} from '../lib/types';

type TabKey = 'info' | 'interactions' | 'commitments' | 'dates' | 'gifts' | 'links' | 'relations';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'info', label: '资料' },
  { key: 'interactions', label: '互动' },
  { key: 'commitments', label: '承诺' },
  { key: 'dates', label: '纪念日' },
  { key: 'gifts', label: '礼物' },
  { key: 'links', label: '链接' },
  { key: 'relations', label: '关系' },
];

const INTERACTION_LABELS: Record<InteractionType, string> = {
  MEETING: '见面', CALL: '通话', MESSAGE: '消息', EMAIL: '邮件', GIFT: '送礼', OTHER: '其他',
};
const DATE_KIND_LABELS: Record<DateKind, string> = {
  BIRTHDAY: '生日', ANNIVERSARY: '纪念日', MEMORIAL: '缅怀', CUSTOM: '自定义',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: contact, isLoading, error } = useContact(id);
  const updateContact = useUpdateContact();
  const deleteContact = useDeleteContact();
  const archiveContact = useArchiveContact();
  const [tab, setTab] = useState<TabKey>('info');
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return <div className="flex justify-center py-16"><Spinner className="h-8 w-8" /></div>;
  }
  if (error || !contact) {
    return (
      <Card className="p-6">
        <p className="text-sm text-red-600 dark:text-red-400">{errMsg(error ?? new Error('联系人不存在'))}</p>
        <Button className="mt-4" variant="secondary" onClick={() => navigate('/contacts')}>返回列表</Button>
      </Card>
    );
  }
  const c = contact;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
        <Link to="/contacts" className="hover:text-indigo-600 dark:hover:text-indigo-400">人脉</Link>
        <Icon name="chevronRight" className="h-3.5 w-3.5" />
        <span className="text-slate-700 dark:text-slate-200">{c.name}</span>
      </div>

      <Card className="p-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xl font-semibold text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
            {c.name.slice(0, 1)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">{c.name}</h1>
              {c.isFavorite && <Icon name="star" className="h-4 w-4 text-amber-500" />}
              {c.archivedAt && <Badge className="bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400">已归档</Badge>}
            </div>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {[c.company, c.position].filter(Boolean).join(' · ') || '未填写单位'}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge className="bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300">
                亲密度 {'★'.repeat(Math.min(5, Math.max(1, c.intimacyLevel)))}
              </Badge>
              <Badge className="bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                上次联系 {fmtDate(c.lastContactAt)}
              </Badge>
              {c.nextContactAt && (
                <Badge className="bg-emerald-50 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300">
                  下次联系 {fmtDate(c.nextContactAt)}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
              <Icon name="edit" className="mr-1 h-4 w-4" />编辑
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => updateContact.mutateAsync({ id: c.id, body: { isFavorite: !c.isFavorite } }).catch(() => undefined)}
            >
              <Icon name="star" className="mr-1 h-4 w-4" />{c.isFavorite ? '取消收藏' : '收藏'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => archiveContact.mutateAsync(c.id).catch(() => undefined)}
            >
              {c.archivedAt ? '恢复' : '归档'}
            </Button>
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
              <Icon name="trash" className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'info' && <InfoSection c={c} />}
      {tab === 'interactions' && <InteractionsSection c={c} />}
      {tab === 'commitments' && <CommitmentsSection c={c} />}
      {tab === 'dates' && <DatesSection c={c} />}
      {tab === 'gifts' && <GiftsSection c={c} />}
      {tab === 'links' && <LinksSection c={c} />}
      {tab === 'relations' && <RelationsSection c={c} />}

      <ContactForm open={editOpen} onClose={() => setEditOpen(false)} contact={c} />

      <ConfirmDialog
        open={confirmDelete}
        title="删除联系人"
        message={`确定删除联系人「${c.name}」吗？相关互动、承诺等记录将一并删除。`}
        confirmText="删除"
        danger
        busy={deleteContact.isPending}
        onConfirm={() => {
          void deleteContact.mutateAsync(c.id)
            .then(() => navigate('/contacts'))
            .catch(() => setConfirmDelete(false));
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ==================== 资料 ====================

function InfoSection({ c }: { c: ContactDetailT }) {
  const rows: Array<[string, string]> = [
    ['手机', c.phone ?? '—'],
    ['邮箱', c.email ?? '—'],
    ['微信', c.wechat ?? '—'],
    ['生日', c.birthday
      ? (c.isLunarBirthday
        ? `农历 ${c.lunarMonth ?? '?'} 月 ${c.lunarDay ?? '?'} 日`
        : fmtDate(c.birthday))
      : '—'],
    ['联系节奏', c.contactIntervalDays ? `每 ${c.contactIntervalDays} 天` : '—'],
  ];
  return (
    <Card className="p-5">
      <SectionTitle title="基本资料" />
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt className="text-xs text-slate-400 dark:text-slate-500">{k}</dt>
            <dd className="mt-0.5 text-sm text-slate-700 dark:text-slate-200">{v}</dd>
          </div>
        ))}
      </dl>
      {c.howMet && (
        <div className="mt-4">
          <div className="text-xs text-slate-400 dark:text-slate-500">认识经过</div>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-200">{c.howMet}</p>
        </div>
      )}
      {c.notes && (
        <div className="mt-4">
          <div className="text-xs text-slate-400 dark:text-slate-500">备注</div>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-200">{c.notes}</p>
        </div>
      )}
    </Card>
  );
}

// ==================== 互动 ====================

function InteractionsSection({ c }: { c: ContactDetailT }) {
  const createInteraction = useCreateInteraction();
  const deleteInteraction = useDeleteInteraction();
  const [type, setType] = useState<InteractionType>('MEETING');
  const [occurredAt, setOccurredAt] = useState(new Date().toISOString().slice(0, 16));
  const [summary, setSummary] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setError(null);
    if (!summary.trim()) { setError('请填写互动摘要'); return; }
    try {
      // CreateInteractionBody：type/occurredAt/summary 必填，notes 仅已填时提交
      const body: CreateInteractionBody = {
        type,
        occurredAt: new Date(occurredAt).toISOString(),
        summary: summary.trim(),
      };
      if (notes.trim()) body.notes = notes.trim();
      await createInteraction.mutateAsync({ contactId: c.id, body });
      setSummary('');
      setNotes('');
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <Card className="p-5">
      <SectionTitle title={`互动记录（${c.interactions.length}）`} />
      <div className="mb-4 grid gap-2 sm:grid-cols-4">
        <Select value={type} onChange={(e) => setType(e.target.value as InteractionType)}>
          {(Object.keys(INTERACTION_LABELS) as InteractionType[]).map((t) => (
            <option key={t} value={t}>{INTERACTION_LABELS[t]}</option>
          ))}
        </Select>
        <Input
          type="datetime-local"
          value={occurredAt}
          onChange={(e) => setOccurredAt(e.target.value)}
          className="sm:col-span-1"
        />
        <Input
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="摘要 *"
        />
        <Button onClick={() => void add()} loading={createInteraction.isPending}>记录互动</Button>
      </div>
      {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {c.interactions.length === 0 ? (
        <EmptyState title="暂无互动记录" description="记录每一次见面、通话或消息" />
      ) : (
        <ul className="space-y-2">
          {c.interactions.map((i) => (
            <li key={i.id} className="flex items-start gap-3 rounded-lg border border-slate-100 p-3 dark:border-slate-700">
              <Badge className="shrink-0 bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300">
                {INTERACTION_LABELS[i.type]}
              </Badge>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 dark:text-slate-100">{i.summary ?? '—'}</p>
                <p className="mt-0.5 text-xs text-slate-400">{fmtDateTime(i.occurredAt)}</p>
                {i.notes && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{i.notes}</p>}
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label="删除互动"
                onClick={() => deleteInteraction.mutateAsync(i.id).catch(() => undefined)}
              >
                <Icon name="trash" className="h-4 w-4 text-red-500" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ==================== 承诺 ====================

function CommitmentsSection({ c }: { c: ContactDetailT }) {
  const createCommitment = useCreateCommitment();
  const completeCommitment = useCompleteCommitment();
  const deleteCommitment = useDeleteCommitment();
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setError(null);
    if (!title.trim()) { setError('请填写承诺内容'); return; }
    try {
      // CreateCommitmentBody：title/contactId 必填，dueDate 仅已填时提交
      const body: CreateCommitmentBody = { title: title.trim(), contactId: c.id };
      if (dueDate) body.dueDate = dueDate;
      await createCommitment.mutateAsync(body);
      setTitle('');
      setDueDate('');
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <Card className="p-5">
      <SectionTitle title={`未完成承诺（${c.openCommitments.length}）`} />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="承诺内容 *"
          className="min-w-[200px] flex-1"
        />
        <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-40" />
        <Button onClick={() => void add()} loading={createCommitment.isPending}>添加</Button>
      </div>
      {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {c.openCommitments.length === 0 ? (
        <EmptyState title="暂无未完成承诺" description="答应别人的事情，记得兑现" />
      ) : (
        <ul className="space-y-2">
          {c.openCommitments.map((m) => (
            <li key={m.id} className="flex items-center gap-3 rounded-lg border border-slate-100 p-3 dark:border-slate-700">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 dark:text-slate-100">{m.title}</p>
                {m.dueDate && (
                  <p className="mt-0.5 text-xs text-slate-400">截止 {fmtDate(m.dueDate)}</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => completeCommitment.mutateAsync(m.id).catch(() => undefined)}
              >
                <Icon name="check" className="mr-1 h-4 w-4" />完成
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label="删除承诺"
                onClick={() => deleteCommitment.mutateAsync(m.id).catch(() => undefined)}
              >
                <Icon name="trash" className="h-4 w-4 text-red-500" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ==================== 重要日期 ====================

function DatesSection({ c }: { c: ContactDetailT }) {
  const createDate = useCreateImportantDate();
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<DateKind>('BIRTHDAY');
  const [gregorianDate, setGregorianDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setError(null);
    if (!title.trim()) { setError('请填写名称'); return; }
    if (!gregorianDate) { setError('请选择日期'); return; }
    try {
      // CreateImportantDateBody：title/kind/gregorianDate 必填
      const body: CreateImportantDateBody = {
        title: title.trim(),
        kind,
        gregorianDate,
        contactId: c.id,
      };
      await createDate.mutateAsync(body);
      setTitle('');
      setGregorianDate('');
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <Card className="p-5">
      <SectionTitle title={`重要日期（${c.importantDates.length}）`} />
      <div className="mb-4 grid gap-2 sm:grid-cols-4">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="名称 *" />
        <Select value={kind} onChange={(e) => setKind(e.target.value as DateKind)}>
          {(Object.keys(DATE_KIND_LABELS) as DateKind[]).map((k) => (
            <option key={k} value={k}>{DATE_KIND_LABELS[k]}</option>
          ))}
        </Select>
        <Input type="date" value={gregorianDate} onChange={(e) => setGregorianDate(e.target.value)} />
        <Button onClick={() => void add()} loading={createDate.isPending}>添加</Button>
      </div>
      {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {c.importantDates.length === 0 ? (
        <EmptyState title="暂无重要日期" description="生日、纪念日都会在这里提醒" />
      ) : (
        <ul className="space-y-2">
          {c.importantDates.map((d) => (
            <li key={d.id} className="flex items-center gap-3 rounded-lg border border-slate-100 p-3 dark:border-slate-700">
              <Badge className="shrink-0 bg-amber-50 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300">
                {DATE_KIND_LABELS[d.kind]}
              </Badge>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 dark:text-slate-100">{d.title}</p>
                <p className="mt-0.5 text-xs text-slate-400">
                  {d.isLunar ? `农历 ${d.lunarMonth ?? '?'} 月 ${d.lunarDay ?? '?'} 日` : fmtDate(d.gregorianDate)}
                  {d.nextOccurrence && ` · 下次 ${fmtDate(d.nextOccurrence)}`}
                  {d.daysUntil != null && `（还有 ${d.daysUntil} 天）`}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ==================== 礼物 ====================

function GiftsSection({ c }: { c: ContactDetailT }) {
  const createGift = useCreateGift();
  const deleteGift = useDeleteGift();
  const [direction, setDirection] = useState<GiftDirection>('GIVEN');
  const [title, setTitle] = useState('');
  const [givenAt, setGivenAt] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setError(null);
    if (!title.trim()) { setError('请填写礼物名称'); return; }
    if (!givenAt) { setError('请选择日期'); return; }
    try {
      // CreateGiftBody：direction/title/givenAt 必填
      const body: CreateGiftBody = {
        direction,
        title: title.trim(),
        givenAt,
        contactId: c.id,
      };
      await createGift.mutateAsync(body);
      setTitle('');
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <Card className="p-5">
      <SectionTitle title={`礼物记录（${c.gifts.length}）`} />
      <div className="mb-4 grid gap-2 sm:grid-cols-4">
        <Select value={direction} onChange={(e) => setDirection(e.target.value as GiftDirection)}>
          <option value="GIVEN">送出</option>
          <option value="RECEIVED">收到</option>
        </Select>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="礼物名称 *" />
        <Input type="date" value={givenAt} onChange={(e) => setGivenAt(e.target.value)} />
        <Button onClick={() => void add()} loading={createGift.isPending}>添加</Button>
      </div>
      {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {c.gifts.length === 0 ? (
        <EmptyState title="暂无礼物记录" description="记录互赠的礼物，人情往来不忘记" />
      ) : (
        <ul className="space-y-2">
          {c.gifts.map((g) => (
            <li key={g.id} className="flex items-center gap-3 rounded-lg border border-slate-100 p-3 dark:border-slate-700">
              <Icon name="gift" className="h-4 w-4 shrink-0 text-pink-500" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 dark:text-slate-100">
                  {g.title}
                  <span className="ml-2 text-xs text-slate-400">
                    {g.direction === 'GIVEN' ? '送出' : '收到'} · {fmtDate(g.givenAt)}
                  </span>
                </p>
                {g.occasion && <p className="mt-0.5 text-xs text-slate-400">{g.occasion}</p>}
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label="删除礼物"
                onClick={() => deleteGift.mutateAsync(g.id).catch(() => undefined)}
              >
                <Icon name="trash" className="h-4 w-4 text-red-500" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ==================== 社交链接 ====================

function LinksSection({ c }: { c: ContactDetailT }) {
  const createLink = useCreateLink();
  const deleteLink = useDeleteLink();
  const [platform, setPlatform] = useState('');
  const [url, setUrl] = useState('');
  const [handle, setHandle] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setError(null);
    if (!platform.trim()) { setError('请填写平台名称'); return; }
    if (!url.trim()) { setError('请填写链接地址'); return; }
    try {
      // CreateLinkBody：platform/url 必填，handle 仅已填时提交
      const body: CreateLinkBody = { platform: platform.trim(), url: url.trim() };
      if (handle.trim()) body.handle = handle.trim();
      await createLink.mutateAsync({ contactId: c.id, body });
      setPlatform('');
      setUrl('');
      setHandle('');
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <Card className="p-5">
      <SectionTitle title={`社交链接（${c.socialLinks.length}）`} />
      <div className="mb-4 grid gap-2 sm:grid-cols-4">
        <Input value={platform} onChange={(e) => setPlatform(e.target.value)} placeholder="平台 *" />
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="链接地址 *" />
        <Input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="账号（可选）" />
        <Button onClick={() => void add()} loading={createLink.isPending}>添加</Button>
      </div>
      {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {c.socialLinks.length === 0 ? (
        <EmptyState title="暂无社交链接" description="添加对方的社交主页" />
      ) : (
        <ul className="space-y-2">
          {c.socialLinks.map((l) => (
            <li key={l.id} className="flex items-center gap-3 rounded-lg border border-slate-100 p-3 dark:border-slate-700">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 dark:text-slate-100">
                  {l.platform}
                  {l.handle && <span className="ml-2 text-xs text-slate-400">{l.handle}</span>}
                </p>
                <a
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-0.5 block truncate text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  {l.url}
                </a>
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label="删除链接"
                onClick={() => deleteLink.mutateAsync(l.id).catch(() => undefined)}
              >
                <Icon name="trash" className="h-4 w-4 text-red-500" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ==================== 关系 ====================

function RelationsSection({ c }: { c: ContactDetailT }) {
  const createRelation = useCreateRelation();
  const deleteRelation = useDeleteRelation();
  const { data: contacts } = useContacts({ pageSize: 100, archived: 'false' });
  const [relatedContactId, setRelatedContactId] = useState('');
  const [relation, setRelation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const options = (contacts?.items ?? []).filter((x) => x.id !== c.id);

  async function add() {
    setError(null);
    if (!relatedContactId) { setError('请选择关联联系人'); return; }
    if (!relation.trim()) { setError('请填写关系'); return; }
    try {
      // CreateRelationBody：relatedContactId/relation 必填
      const body: CreateRelationBody = {
        relatedContactId,
        relation: relation.trim(),
      };
      await createRelation.mutateAsync({ contactId: c.id, body });
      setRelatedContactId('');
      setRelation('');
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <Card className="p-5">
      <SectionTitle title={`人际关系（${c.relations.length}）`} />
      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        <Select value={relatedContactId} onChange={(e) => setRelatedContactId(e.target.value)}>
          <option value="">选择联系人 *</option>
          {options.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </Select>
        <Input value={relation} onChange={(e) => setRelation(e.target.value)} placeholder="关系，如：同事、同学 *" />
        <Button onClick={() => void add()} loading={createRelation.isPending}>添加</Button>
      </div>
      {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {c.relations.length === 0 ? (
        <EmptyState title="暂无关系" description="建立联系人之间的关系网络" />
      ) : (
        <ul className="space-y-2">
          {c.relations.map((r) => (
            <li key={r.id} className="flex items-center gap-3 rounded-lg border border-slate-100 p-3 dark:border-slate-700">
              <Icon name="contacts" className="h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 dark:text-slate-100">
                  {r.relation} ·{' '}
                  <Link to={`/contacts/${r.other.id}`} className="text-indigo-600 hover:underline dark:text-indigo-400">
                    {r.other.name}
                  </Link>
                </p>
                {r.notes && <p className="mt-0.5 text-xs text-slate-400">{r.notes}</p>}
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label="删除关系"
                onClick={() => deleteRelation.mutateAsync({ contactId: c.id, rid: r.id }).catch(() => undefined)}
              >
                <Icon name="trash" className="h-4 w-4 text-red-500" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
